import { motion } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

interface RegistrationMeterProps {
  totalCapacity: number;
  currentRegistrations: number;
  reservedSpots?: number;
  className?: string;
  showButton?: boolean;
  onRegister?: () => void;
}

export default function RegistrationMeter({ 
  totalCapacity, 
  currentRegistrations, 
  reservedSpots = 0,
  className = '',
  showButton = false,
  onRegister
}: RegistrationMeterProps) {
  const navigate = useNavigate();
  
  // Count reserved spots as part of current registrations for display purposes
  const effectiveRegistrations = currentRegistrations + reservedSpots;
  const percentageFilled = Math.min((effectiveRegistrations / totalCapacity) * 100, 100);
  const spotsLeft = Math.max(totalCapacity - effectiveRegistrations, 0);
  const isAlmostFull = percentageFilled >= 75;
  const isFull = percentageFilled >= 100;

  const getStatusColor = () => {
    if (isFull) return 'text-red-500';
    if (isAlmostFull) return 'text-amber-500';
    return 'text-emerald-500';
  };

  const getProgressColor = () => {
    if (isFull) return 'bg-red-500';
    if (isAlmostFull) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  const handleClick = () => {
    if (isFull) {
      toast.error('Registration is closed. Maximum capacity has been reached.');
      return;
    }
    
    if (onRegister) {
      onRegister();
    } else {
      navigate('/registration');
    }
  };

  return (
    <div className={`bg-black/60 backdrop-blur-md rounded-xl p-6 border border-white/10 ${className}`}>
      {isFull ? (
        <div className="text-center px-2 sm:px-4">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Registration Closed</h3>
          <p className="text-golf-sand">
            The tournament has reached its maximum capacity of {totalCapacity} players.
            Please check back next year.
          </p>
        </div>
      ) : (
        <>
          <div className="flex justify-between items-center mb-2">
            <span className="text-lg font-semibold text-white">Registration Status</span>
            <span className={`${getStatusColor()} font-bold text-lg`}>
              {`${effectiveRegistrations}/${totalCapacity}`}
            </span>
          </div>
          
          <div className="relative h-4 bg-white/20 rounded-full overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${percentageFilled}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`absolute left-0 top-0 h-full rounded-full ${getProgressColor()}`}
            />
          </div>

          <div className="mb-6">
            {isAlmostFull ? (
              <div className="flex items-start gap-2 text-amber-400 text-sm sm:text-base">
                <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-1" />
                <p className="text-sm font-medium">
                  Hurry! Only {spotsLeft} spots remaining!
                </p>
              </div>
            ) : (
              <p className="text-sm sm:text-base text-golf-sand font-medium">
                {spotsLeft} spots available. Secure your spot today!
              </p>
            )}
          </div>

          {showButton && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="w-full modern-button py-3 text-white font-medium text-lg"
              onClick={handleClick}
            >
              Register Now
            </motion.button>
          )}
        </>
      )}
    </div>
  );
}